import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash, ArrowLeft } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import useAuthStore from '../store/authStore';

import { notify } from '../lib/notify';
import { ProductPicker } from '../components/ProductPicker';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateQuotation = () => {
  const { user } = useAuthStore();
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transportation, setTransportation] = useState('Included');
  const [paymentTerms, setPaymentTerms] = useState('100% Advance with PO');
  const [customPayment, setCustomPayment] = useState('');
  const [warrantyYears, setWarrantyYears] = useState(1);
  const [installation, setInstallation] = useState('By Dealer');
  const [deliveryDays, setDeliveryDays] = useState(45);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState(null); // { draft } | null
  const navigate = useNavigate();
  const tp = useTenantPath();
  const location = useLocation();

  useEffect(() => {
    fetchLeads();
    fetchProducts();
    
    // Pre-select lead if coming from leads page
    if (location.state?.leadId) {
      setSelectedLead(location.state.leadId);
    }
    // Refetch when window regains focus (so newly added leads/products show up without reload)
    const onFocus = () => { fetchLeads(); fetchProducts(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [location.state]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // === DRAFT AUTO-SAVE ===
  // Build serializable form state
  const buildFormState = () => ({
    items,
    transportation,
    paymentTerms,
    customPayment,
    warrantyYears,
    installation,
    deliveryDays,
  });

  const saveDraft = async () => {
    if (!selectedLead) return; // need a lead to key the draft
    setDraftSaving(true);
    try {
      const r = await axios.post(`${API}/quotation-drafts`, {
        lead_id: selectedLead,
        form_state: buildFormState(),
      });
      setDraftSavedAt(r.data.saved_at);
    } catch (e) {
      // soft fail — don't interrupt user
      console.warn('Draft save failed', e);
    } finally {
      setDraftSaving(false);
    }
  };

  // Check for existing draft when lead is selected
  useEffect(() => {
    if (!selectedLead) {
      setDraftPrompt(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/quotation-drafts/by-lead/${selectedLead}`);
        if (active && r.data?.draft && (items.length === 0)) {
          setDraftPrompt({ draft: r.data.draft });
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { active = false; };
    // eslint-disable-next-line
  }, [selectedLead]);

  // Auto-save every 30s while a lead is selected and form has items
  useEffect(() => {
    if (!selectedLead) return undefined;
    const id = setInterval(() => {
      if (items.length > 0) saveDraft();
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [selectedLead, items, transportation, paymentTerms, customPayment, warrantyYears, installation, deliveryDays]);

  const resumeDraft = () => {
    if (!draftPrompt?.draft?.form_state) return;
    const fs = draftPrompt.draft.form_state;
    setItems(fs.items || []);
    setTransportation(fs.transportation || 'Included');
    setPaymentTerms(fs.paymentTerms || '100% Advance with PO');
    setCustomPayment(fs.customPayment || '');
    setWarrantyYears(fs.warrantyYears || 1);
    setInstallation(fs.installation || 'By Dealer');
    setDeliveryDays(fs.deliveryDays || 45);
    setDraftSavedAt(draftPrompt.draft.updated_at);
    setDraftPrompt(null);
  };

  const discardDraft = async () => {
    if (selectedLead) {
      try { await axios.delete(`${API}/quotation-drafts/by-lead/${selectedLead}`); } catch (e) { /* ignore */ }
    }
    setDraftPrompt(null);
    setDraftSavedAt(null);
  };

  const getTransportRate = (t) => t === 'Included' ? 8 : t === 'Partial' ? 4 : 0;

  // --- Per-role auto-approve limits (President-configurable, GET /discount-limits) ---
  const [roleLimits, setRoleLimits] = useState(null);
  useEffect(() => {
    axios.get(`${API}/discount-limits`).then((r) => setRoleLimits(r.data)).catch(() => setRoleLimits(null));
  }, []);
  const myLimit = roleLimits ? (roleLimits.limits[user?.role] ?? roleLimits.limits['Sales Executive']) : 0;
  const approverFor = (pct) => {
    if (!roleLimits) return 'a senior approver';
    return roleLimits.ladder.find((r) => r !== 'Sales Executive' && (roleLimits.limits[r] ?? 0) >= pct) || 'President';
  };
  const [floorPct, setFloorPct] = useState(0); // absolute company-wide cap
  useEffect(() => {
    axios.get(`${API}/company-settings`).then(r => setFloorPct(Number(r.data?.discount_floor_pct || 0)))
      .catch(() => setFloorPct(0));
  }, []);

  // Given catalog price (MRP), returns the effective discount% for a quoted price
  const pctFromPrice = (mrp, quoted) => {
    if (!mrp || mrp <= 0) return 0;
    const p = Math.max(0, Math.min(100, ((mrp - quoted) / mrp) * 100));
    return Math.round(p * 100) / 100;
  };
  // Given catalog price + discount%, returns the quoted price
  const priceFromPct = (mrp, pct) => {
    if (!mrp || mrp <= 0) return 0;
    const clamped = Math.max(0, Math.min(100, pct));
    return Math.round((mrp * (100 - clamped) / 100) * 100) / 100;
  };

  const recalcItem = (item, transportOpt) => {
    const taxableAmount = item.price * item.quantity;
    const discountPct = item.item_discount_percent || 0;
    // NOTE: the discount is now baked into item.price (see updateItem below), so
    // item_discount_amount stays 0 in the calculation to avoid double-counting.
    // We keep item_discount_percent stored on the item so backend can see intent.
    const itemDiscountAmt = 0;
    const discountedAmount = taxableAmount;
    const tRate = getTransportRate(transportOpt || transportation);
    const transportCharge = (discountedAmount * tRate) / 100;
    // Feb 2026 (iter82) — GST is now applied to (item + transport) at the
    // item's own tax rate for the item portion, and at a fixed 18% for the
    // transport portion (composite supply under Indian GST). Backend uses
    // the same math; keep this in sync with `create_quotation` server-side.
    const itemGst = (discountedAmount * (item.tax_rate || 18)) / 100;
    const transportGst = (transportCharge * 18) / 100;
    const taxAmount = itemGst + transportGst;
    return {
      ...item,
      item_discount_percent: discountPct,
      taxable_amount: taxableAmount,
      item_discount_amount: itemDiscountAmt,
      discounted_amount: discountedAmount,
      tax_amount: taxAmount,
      transport_charge: transportCharge,
      transport_gst_amount: transportGst,
      total: discountedAmount + taxAmount + transportCharge
    };
  };

  const addItem = () => {
    setItems([...items, { 
      product_id: '', 
      product_name: '', 
      quantity: 1, 
      price: 0, 
      hsn_code: '',
      tax_rate: 0,
      taxable_amount: 0,
      item_discount_percent: 0,
      item_discount_amount: 0,
      discounted_amount: 0,
      tax_amount: 0,
      transport_charge: 0,
      total: 0 
    }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].mrp = product.price;             // remember catalog price
        newItems[index].price = product.price;
        newItems[index].item_discount_percent = 0;
        newItems[index].hsn_code = product.hsn_code || '';
        newItems[index].tax_rate = product.tax_bracket || 18;
        newItems[index] = recalcItem(newItems[index]);
      }
    } else if (field === 'price') {
      // Editing price → derive discount % from MRP
      const mrp = newItems[index].mrp || newItems[index].price;
      newItems[index].mrp = mrp;
      const clamped = Math.max(0, Math.min(mrp, Number(value) || 0));
      newItems[index].price = clamped;
      newItems[index].item_discount_percent = pctFromPrice(mrp, clamped);
      newItems[index] = recalcItem(newItems[index]);
    } else if (field === 'item_discount_percent') {
      // Editing discount % → derive price from MRP
      const mrp = newItems[index].mrp || newItems[index].price;
      newItems[index].mrp = mrp;
      const pct = Math.max(0, Math.min(100, Number(value) || 0));
      newItems[index].item_discount_percent = pct;
      newItems[index].price = priceFromPct(mrp, pct);
      newItems[index] = recalcItem(newItems[index]);
    } else if (field === 'quantity') {
      newItems[index] = recalcItem(newItems[index]);
    }

    setItems(newItems);
  };

  // Highest discount % across all lines — drives the warning banner
  const maxDiscountPct = items.reduce((m, it) => Math.max(m, it.item_discount_percent || 0), 0);
  const overCeiling = false; // single-hop routing: any discount within the company max is allowed, just routed
  const overFloor = floorPct > 0 && maxDiscountPct > floorPct;
  const escalationRequired = !overFloor && !!roleLimits && maxDiscountPct > myLimit;

  const handleTransportChange = (opt) => {
    setTransportation(opt);
    setItems(prev => prev.map(item => item.product_id ? recalcItem(item, opt) : item));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.taxable_amount || 0), 0);
  };

  const calculateTotalDiscount = () => {
    return items.reduce((sum, item) => sum + (item.item_discount_amount || 0), 0);
  };

  const calculateTotalTax = () => {
    return items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  };

  const calculateTotalTransport = () => {
    return items.reduce((sum, item) => sum + (item.transport_charge || 0), 0);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate: at least one product line item is required
    if (items.length === 0) {
      notify('Please add at least one product to the quotation before submitting.');
      return;
    }
    // Validate: every line item must have a product selected and a positive quantity
    const invalidIdx = items.findIndex(it => !it.product_id || !it.quantity || it.quantity <= 0);
    if (invalidIdx !== -1) {
      notify(`Line item #${invalidIdx + 1} is incomplete. Please select a product and enter a valid quantity (or remove the empty row).`);
      return;
    }

    // Discount authority gate — Feb 2026: client no longer hard-blocks over
    // the escalate ceiling. Any discount is *creatable*; the backend routes
    // the quote to the required approver tier on submit, and PDF/share stay
    // locked until approval lands. We still block over the company floor (a
    // hard business rule) here for a friendlier UX than a 400 round-trip.
    if (overFloor) {
      notify(`One or more lines exceed the company-wide maximum discount of ${floorPct}% set by the President.`);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/quotations`, {
        lead_id: selectedLead,
        items: items,
        discount: calculateTotalDiscount(),
        transportation,
        payment_terms: paymentTerms === 'Custom' ? customPayment : paymentTerms,
        warranty_years: warrantyYears,
        installation,
        delivery_days: deliveryDays
      });

      const quotationId = response.data.id;

      // Submit for approval
      await axios.post(`${API}/quotations/${quotationId}/submit`);

      // Clear draft on successful submit
      if (selectedLead) {
        try { await axios.delete(`${API}/quotation-drafts/by-lead/${selectedLead}`); } catch (e) { /* ignore */ }
      }

      notify('Quotation created and submitted for approval!');
      navigate(tp('/quotations'));
    } catch (error) {
      console.error('Error creating quotation:', error);
      notify('Error creating quotation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="create-quotation-page">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(tp('/quotations'))}
          className="w-10 h-10 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="page-title">
            Create Quotation
          </h1>
          <p className="text-[#6D8E87]">Build a new quotation for your customer</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} onBlur={() => { if (selectedLead && items.length > 0) saveDraft(); }}>
        {/* Draft Resume Prompt */}
        {draftPrompt?.draft && (
          <div className="medical-card p-4 mb-4 border-l-4 border-amber-400 bg-amber-50" data-testid="draft-resume-prompt">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-[#052A23]">A saved draft exists for this lead</div>
                <div className="text-xs text-[#6D8E87]">
                  Last saved: {new Date(draftPrompt.draft.updated_at).toLocaleString()} —
                  drafts older than 3 days are auto-discarded.
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={resumeDraft}
                  className="px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-medium hover:bg-[#008f86]"
                  data-testid="resume-draft-btn">Resume Draft</button>
                <button type="button" onClick={discardDraft}
                  className="px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#3B5A54] hover:bg-[#F4F9F7]"
                  data-testid="discard-draft-btn">Discard</button>
              </div>
            </div>
          </div>
        )}

        {/* Auto-save Indicator */}
        {selectedLead && items.length > 0 && (
          <div className="flex items-center justify-end gap-2 mb-2 text-xs text-[#6D8E87]" data-testid="autosave-indicator">
            {draftSaving ? (
              <span>Saving draft…</span>
            ) : draftSavedAt ? (
              <span>✓ Draft saved at {new Date(draftSavedAt).toLocaleTimeString()}</span>
            ) : (
              <span>Auto-save will start in a moment…</span>
            )}
            <button type="button" onClick={saveDraft}
              className="ml-2 px-2 py-1 border border-[#D5E8E4] rounded text-[#3B5A54] hover:bg-[#F4F9F7]"
              data-testid="save-draft-now-btn">Save Draft Now</button>
          </div>
        )}
        <div className="medical-card p-6 mb-6">
          <h3 className="text-xl font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>
            Customer Information
          </h3>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#3B5A54]">Select Lead / Customer</label>
              <button type="button" onClick={() => window.open('/leads', '_blank')}
                className="text-xs text-[#00a69c] font-semibold hover:underline" data-testid="add-new-lead-link">
                + Add New Customer
              </button>
            </div>
            <select
              value={selectedLead}
              onChange={(e) => setSelectedLead(e.target.value)}
              className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
              required
              data-testid="lead-select"
            >
              <option value="">Choose a customer...</option>
              {leads.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.name} - {lead.company}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[#6D8E87] mt-1">Tip: After adding a new customer in the other tab, return here and the list will refresh automatically.</p>
          </div>
        </div>

        <div className="medical-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              Items
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.open('/products', '_blank')}
                className="flex items-center gap-1 px-3 py-2 text-xs text-[#00a69c] border border-[#00a69c] rounded-lg hover:bg-[#E2F3F0] transition font-semibold"
                data-testid="add-new-product-link"
                title="Open Products in new tab to add"
              >
                <Plus size={14} weight="bold" /> Add New Product
              </button>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center gap-2 px-4 py-2 bg-[#E2F3F0] text-[#00a69c] rounded-lg hover:bg-[#B5D9D2] transition"
                data-testid="add-item-button"
              >
                <Plus size={20} />
                Add Item
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-[#D5E8E4] rounded-lg bg-[#F4F9F7]" data-testid="empty-items-hint">
              <p className="text-[#6D8E87] mb-1 font-medium">No products added yet</p>
              <p className="text-xs text-[#6D8E87]">Click <span className="font-semibold text-[#00a69c]">&quot;Add Item&quot;</span> above to add at least one product before submitting.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Discount authority banner — Feb 2026: `overCeiling` is now
                  informational, not blocking. The quote is still creatable;
                  it just needs a higher-tier approver before it can be
                  shared or downloaded. Only `overFloor` remains a hard stop. */}
              {maxDiscountPct > 0 && (
                <div
                  className={`p-3 rounded-lg text-sm border ${
                    overFloor
                      ? 'bg-rose-50 border-rose-200 text-rose-700'
                      : (overCeiling || escalationRequired)
                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  }`}
                  data-testid="discount-authority-banner"
                >
                  {overFloor && (
                    <>Highest discount {maxDiscountPct.toFixed(2)}% exceeds the company-wide maximum of {floorPct}%. Please reduce it.</>
                  )}
                  {!overFloor && !overCeiling && escalationRequired && (
                    <>This quote will need approval from <strong>{approverFor(maxDiscountPct)}</strong> because the highest discount ({maxDiscountPct.toFixed(2)}%) exceeds your auto-approval limit of {myLimit}%. It stays locked (no download / share) until approved.</>
                  )}
                  {!overFloor && !overCeiling && !escalationRequired && (
                    <>Discount {maxDiscountPct.toFixed(2)}% is within your auto-approval limit ({myLimit}%).</>
                  )}
                </div>
              )}
              {items.map((item, index) => (
                <div key={index} className="p-4 bg-[#F4F9F7] rounded-lg" data-testid={`item-${index}`}>
                  <div className="flex gap-3 items-start mb-2">
                    <div className="flex-1">
                      <ProductPicker
                        products={products}
                        value={item.product_id}
                        onChange={(id) => updateItem(index, 'product_id', id)}
                        testid={`product-select-${index}`}
                      />
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none text-center"
                        placeholder="Qty"
                        data-testid={`quantity-input-${index}`}
                      />
                      <span className="text-[10px] text-[#6D8E87] block text-center mt-0.5">Qty</span>
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none text-right"
                        placeholder="Price"
                        data-testid={`price-input-${index}`}
                      />
                      <span className="text-[10px] text-[#6D8E87] block text-center mt-0.5">
                        Unit Price
                        {item.mrp > 0 && item.mrp !== item.price && (
                          <span className="block text-amber-600">MRP ₹{Number(item.mrp).toLocaleString('en-IN')}</span>
                        )}
                      </span>
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={item.item_discount_percent || 0}
                        onChange={(e) => updateItem(index, 'item_discount_percent', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none text-center bg-amber-50"
                        placeholder="0"
                        data-testid={`discount-pct-${index}`}
                      />
                      <span className="text-[10px] text-amber-600 block text-center mt-0.5">Disc %</span>
                    </div>
                    <div className="w-40 flex flex-col items-end">
                      <span className="text-lg font-semibold text-[#00a69c]">
                        ₹{(item.total || 0).toLocaleString('en-IN')}
                      </span>
                      {item.product_name && (
                        <span className="text-[10px] text-[#6D8E87]">
                          GST {item.tax_rate}%{(item.transport_charge || 0) > 0 ? ` + Trp` : ''}
                          {(item.item_discount_amount || 0) > 0 && <span className="text-amber-600"> | Disc: -₹{item.item_discount_amount.toLocaleString('en-IN')}</span>}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="w-10 h-10 flex items-center justify-center text-[#EF4444] hover:bg-red-50 rounded-lg transition"
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="medical-card p-6 mb-6">
          <h3 className="text-xl font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>
            Summary
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-[#3B5A54]">
              <span>Subtotal (Taxable Amount):</span>
              <span className="font-semibold">₹{calculateSubtotal().toLocaleString('en-IN')}</span>
            </div>
            {calculateTotalDiscount() > 0 && (
              <div className="flex justify-between text-amber-600">
                <span>Total Discount:</span>
                <span className="font-semibold">-₹{calculateTotalDiscount().toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between text-[#3B5A54]">
              <span>Total GST:</span>
              <span className="font-semibold">₹{calculateTotalTax().toLocaleString('en-IN')}</span>
            </div>
            {calculateTotalTransport() > 0 && (
              <div className="flex justify-between text-[#3B5A54]">
                <span>Transportation ({transportation}):</span>
                <span className="font-semibold">₹{calculateTotalTransport().toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between text-2xl font-bold text-[#00a69c] pt-3 border-t-2 border-[#D5E8E4]" style={{ fontFamily: 'Outfit' }}>
              <span>Grand Total:</span>
              <span>₹{calculateTotal().toLocaleString('en-IN')}</span>
            </div>
          </div>
          
          {/* GST Breakdown */}
          {items.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#D5E8E4]">
              <p className="text-xs text-[#6D8E87] mb-2 font-medium">GST Breakdown by Item:</p>
              <div className="space-y-1">
                {items.filter(item => item.product_name).map((item, index) => (
                  <div key={index} className="flex justify-between text-xs text-[#3B5A54]">
                    <span>{item.product_name} ({item.tax_rate}% GST)</span>
                    <span>₹{(item.tax_amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="medical-card p-6 mb-6" data-testid="terms-section">
          <h3 className="text-xl font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>
            Terms &amp; Conditions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Transportation */}
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-2">Transportation *</label>
              <div className="flex gap-2">
                {['Included', 'Free', 'Partial'].map(opt => (
                  <button key={opt} type="button" onClick={() => handleTransportChange(opt)}
                    className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition ${transportation === opt ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}
                    data-testid={`transport-${opt.toLowerCase()}`}>
                    {opt} {opt === 'Included' ? '(+8%)' : opt === 'Partial' ? '(+4%)' : '(0%)'}
                  </button>
                ))}
              </div>
              {(transportation === 'Free' || transportation === 'Partial') && (
                <p className="text-xs text-amber-600 mt-1 font-medium">Requires National Sales Head approval</p>
              )}
            </div>

            {/* Payment Terms */}
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-2">Payment Terms *</label>
              <select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)}
                className="w-full px-4 py-2.5 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                data-testid="payment-terms-select">
                <option value="100% Advance with PO">100% Advance with PO</option>
                <option value="Custom">Custom Entry</option>
              </select>
              {paymentTerms === 'Custom' && (
                <>
                  <input type="text" value={customPayment} onChange={(e) => setCustomPayment(e.target.value)}
                    className="w-full mt-2 px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="Enter custom payment terms..." required
                    data-testid="custom-payment-input" />
                  <p className="text-xs text-amber-600 mt-1 font-medium">Custom terms require National Sales Head approval</p>
                </>
              )}
            </div>

            {/* Warranty */}
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-2">Warranty (Years) *</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(yr => (
                  <button key={yr} type="button" onClick={() => setWarrantyYears(yr)}
                    className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition ${warrantyYears === yr ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}
                    data-testid={`warranty-${yr}`}>
                    {yr}
                  </button>
                ))}
              </div>
              {warrantyYears >= 2 && (
                <p className="text-xs text-amber-600 mt-1 font-medium">Warranty above 1 year requires National Sales Head approval</p>
              )}
            </div>

            {/* Installation */}
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-2">Installation *</label>
              <div className="flex gap-2">
                {['By Dealer', 'By Company', 'By Customer'].map(opt => (
                  <button key={opt} type="button" onClick={() => setInstallation(opt)}
                    className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition ${installation === opt ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}
                    data-testid={`install-${opt.toLowerCase().replace(/\s/g, '-')}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Delivery Period */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#3B5A54] mb-2">Delivery Period (Days) *</label>
              <div className="flex gap-2">
                {[15, 30, 45, 60, 75, 90].map(d => (
                  <button key={d} type="button" onClick={() => setDeliveryDays(d)}
                    className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition ${deliveryDays === d ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}
                    data-testid={`delivery-${d}`}>
                    {d}
                  </button>
                ))}
              </div>
              {deliveryDays === 30 && (
                <p className="text-xs text-amber-600 mt-1 font-medium">30-day delivery requires National Sales Head approval</p>
              )}
              {deliveryDays === 15 && (
                <p className="text-xs text-red-500 mt-1 font-medium">15-day delivery requires National Sales Head + VP approval</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate(tp('/quotations'))}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || items.length === 0 || !selectedLead || overFloor}
            className="btn-primary flex-1"
            data-testid="submit-quotation-button"
          >
            {loading ? 'Creating...' : 'Create & Submit for Approval'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateQuotation;
